import axios from "axios";
import Provider from "~shared/singleton";
import { store } from "../../../redux/store";
import { getRetailerAuthToken } from "~/app/modules/authentication/services";
import { getCookie, setCookie } from "../../utils/cookie";
import {
  isRetailerAuthRefreshFailed,
  redirectToLoginOnRefreshFailure,
} from "../../utils/authSession";
const providerInstance = Provider.getInstance();
/*global BASE_URL, API_VERSION */
/*eslint no-undef: "error"*/
const API = axios.create({
  baseURL: BASE_URL + API_VERSION,
  timeout: 25000,
  headers: {
    Accept: "*/*",
    "Content-Type": "application/json",
  },
});

let refreshPromise = null;
let refreshFailed = false;

function refreshAuthToken() {
  if (refreshFailed) {
    return Promise.reject(new Error("Token refresh failed"));
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  const data = {
    refreshToken: providerInstance.getToken(),
  };

  refreshPromise = store
    .dispatch(getRetailerAuthToken(data))
    .then(() => {
      const authState = store.getState().authentication?.retailer_auth;
      if (isRetailerAuthRefreshFailed(authState)) {
        refreshFailed = true;
        redirectToLoginOnRefreshFailure();
        throw new Error("Token refresh failed");
      }
      const expiryDuration =
        authState?.response?.responseData?.expiryDuration;
      if (expiryDuration) {
        const tokenExpiration = new Date();
        tokenExpiration.setTime(Date.now() + expiryDuration);
        setCookie("tokenExpiration", tokenExpiration);
      }
      return authState?.response?.responseData?.accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}
// const selectAuthToken = () => {
//   return store.getState().authentication?.retailer_auth?.response?.responseData
//     ?.accessToken;
// };
const handleRequest = async (req) => {
  // Check if this is a public endpoint that doesn't require authentication
  const isPublicEndpoint = req.url && (
    req.url.includes('google-calendar/accept-invite') ||
    req.url.includes('google-calendar/complete-oauth')
  );

  // Always set X-TenantSchema if schema is available (needed for public endpoints too)
  const schema = providerInstance.getSchema();
  if (schema) {
    req.headers["X-TenantSchema"] = schema;
  }

  // Add authentication headers only if user is logged in
  if (
    store.getState().authentication?.retailer_auth?.response?.responseData
      ?.accessToken
  ) {
    const getTokenExpiration = getCookie("tokenExpiration");
    const currentDate = new Date().toString();
    const expiryTimeStamp = Date.parse(getTokenExpiration);
    const currentTimeStamp = Date.parse(currentDate);
    if (expiryTimeStamp < currentTimeStamp) {
      try {
        await refreshAuthToken();
      } catch {
        if (refreshFailed) {
          return Promise.reject(new Error("Token refresh failed"));
        }
        return req;
      }
    }
    req.headers["Authorization"] = `Bearer ${
      store.getState().authentication?.retailer_auth?.response?.responseData
        ?.accessToken
    }`;
    req.headers["Session-Key"] = providerInstance.getSessionKey();
    req.headers["User-type"] = "Retailer";
    // req.headers["time_zone"] = providerInstance.getTimeZone();
    return req;
  } else if (isPublicEndpoint) {
    // For public endpoints without auth, ensure we still return the request
    // X-TenantSchema is already set above if schema is available
    return req;
  }
  
  // Always return the request, even if no auth token
  return req;
};
const handleError = (error) => {
  /*eslint no-undef: "error"*/
  let parsed_error = Object.assign({}, error);
  
  // Check if this is a public endpoint
  const isPublicEndpoint = parsed_error?.config?.url && (
    parsed_error.config.url.includes('google-calendar/accept-invite') ||
    parsed_error.config.url.includes('google-calendar/complete-oauth')
  );

  if (
    parsed_error.code == "ECONNABORTED" &&
    !parsed_error.config.__isRetryRequest
  ) {
    parsed_error.config.__isRetryRequest = true;
    return API.request(parsed_error.config);
  } else if (parsed_error?.response?.status == 401 && !isPublicEndpoint) {
    // Only try to refresh token for authenticated endpoints
    // Public endpoints should not trigger token refresh
    if (parsed_error.config.__isRetryRequest || refreshFailed) {
      return Promise.reject(parsed_error?.response?.data);
    }
    return refreshAuthToken()
      .then(() => {
        parsed_error.config.__isRetryRequest = true;
        return API.request(parsed_error.config);
      })
      .catch(() => Promise.reject(parsed_error?.response?.data));
  }
  return Promise.reject(parsed_error?.response?.data);
};
const handleResponse = (response) => {
  return Promise.resolve(response.data);
};
API.interceptors.request.use(handleRequest);
API.interceptors.response.use(handleResponse, handleError);
export default API;